import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { AccountLinkCard, AccountCheckboxCard } from 'web-ui-components/lib/finance/accounts';
import accountPropTypes from '../../../states/accounts/propTypes';
import { formatCreditCardNumber, balanceInPence, showAvailableBalance } from '../../../utils/accountUtils';
import { KAPITI_PRODUCT_CODE } from '../../../config';
import { WithBreakpoints } from '../../../sharedComponents/WithBreakpoints/WithBreakpoints';
import { CREDIT_CARD, PAYEE_PICKER_LAYOUT } from '../../constants';
import accountPickerContent, { contentPropTypes } from './AccountPicker.content';
import withContent from '../../../containers/withContent';

const AccountPickerCardSelector = ({
	account,
	listViewSelected,
	onAccountClick,
	onFavouriteIconClick,
	layoutType,
	getIsAccountSelected,
	canAddMultiplePayees,
}) => {
	const handleOnFavouriteIconClick = onFavouriteIconClick ? () => onFavouriteIconClick(account) : undefined;
	const commonProps = {
		accountName: account.accountName,
		isFavourite: account.isFavourite,
		onFavouriteChange: () => onFavouriteIconClick(account),
		onClick: () => onAccountClick(account),
		productNameOrNickname: account.displayName,
		viewType: listViewSelected ? 'list' : 'grid',
		id: account.id,
	};

	if (layoutType === PAYEE_PICKER_LAYOUT) {
		if (account.type === CREDIT_CARD) {
			if (canAddMultiplePayees) {
				return (
					<AccountCheckboxCard.CreditCard
						{...commonProps}
						balance={balanceInPence(account.currentBalance)}
						cardNumber={formatCreditCardNumber(account.number)}
						onFavouriteChange={handleOnFavouriteIconClick}
						onClick={() => onAccountClick({ payee: account, isInternalAccount: true })}
						isSelected={getIsAccountSelected(account.id)}
					/>
				);
			}
			return (
				<AccountLinkCard.CreditCard
					{...commonProps}
					balance={balanceInPence(account.currentBalance)}
					cardNumber={formatCreditCardNumber(account.number)}
					onFavouriteChange={handleOnFavouriteIconClick}
					onClick={() => onAccountClick({ payee: account, isInternalAccount: true })}
				/>
			);
		}
		if (canAddMultiplePayees) {
			return (
				<AccountCheckboxCard.BankAccount
					{...commonProps}
					accountNumber={Number(account.accountNumber)}
					balance={balanceInPence(account.currentBalance)}
					availableBalance={showAvailableBalance(account)}
					onFavouriteChange={handleOnFavouriteIconClick}
					onClick={() => onAccountClick({ payee: account, isInternalAccount: true })}
					sortCode={Number(account.sortCode)}
					isSelected={getIsAccountSelected(account.id)}
				/>
			);
		}
		return (
			<AccountLinkCard.BankAccount
				{...commonProps}
				accountNumber={Number(account.accountNumber)}
				balance={balanceInPence(account.currentBalance)}
				availableBalance={showAvailableBalance(account)}
				sortCode={Number(account.sortCode)}
				onFavouriteChange={handleOnFavouriteIconClick}
				onClick={() => onAccountClick({ payee: account, isInternalAccount: true })}
			/>
		);
	} else if (account.type === CREDIT_CARD) {
		return (
			<AccountLinkCard.CreditCard
				{...commonProps}
				balance={balanceInPence(account.currentBalance)}
				cardNumber={formatCreditCardNumber(account.number)}
			/>
		);
	} else if (get(account, 'product.code', '') === KAPITI_PRODUCT_CODE) {
		return (
			<AccountLinkCard.International
				{...commonProps}
				internationalBalance={`${account.currentBalance} ${account.currentBalanceCurrency}`}
				availableInternationalBalance={`${account.availableBalance} ${account.availableBalanceCurrency}`}
				IBAN={account.accountNumber}
			/>
		);
	}
	return (
		<AccountLinkCard.BankAccount
			{...commonProps}
			accountNumber={Number(account.accountNumber)}
			balance={balanceInPence(account.currentBalance)}
			availableBalance={showAvailableBalance(account)}
			sortCode={Number(account.sortCode)}
		/>
	);
};

AccountPickerCardSelector.propTypes = {
	account: PropTypes.shape(accountPropTypes).isRequired,
	listViewSelected: PropTypes.bool.isRequired,
	onAccountClick: PropTypes.func,
	onFavouriteIconClick: PropTypes.func.isRequired,
	layoutType: PropTypes.string.isRequired,
	getIsAccountSelected: PropTypes.func.isRequired,
	canAddMultiplePayees: PropTypes.bool,
	content: PropTypes.shape(contentPropTypes),
};

export default WithBreakpoints(withContent({ content: accountPickerContent })(AccountPickerCardSelector));
